import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plane, TowerControl, MapPin, Route, Star } from "lucide-react";
import api from "../lib/api";

/* IndianAirports.com — Airlines serving India (aggregated from airport records).
   Hero follows the user's AirlinesAirports.com masthead design: left-aligned
   brand lockup + tagline + 4 icon quick-links over a tarmac photo, sky shade. */
export default function IndiaAirlines() {
  const [data, setData] = useState(null);

  useEffect(() => {
    document.title = "Airlines at Indian Airports — IndianAirports.com";
    api.get("/india/airlines").then((r) => setData(r.data)).catch(() => setData({ items: [] }));
  }, []);

  if (!data) return <div className="max-w-5xl mx-auto px-5 py-12 text-sm text-zinc-500">Loading airlines…</div>;
  return (
    <div data-testid="india-airlines-page">
      {/* Hero — AirlinesAirports-style masthead, sunrise sky shade */}
      <div className="relative border-b border-zinc-200" data-testid="india-airlines-hero">
        <img src="https://images.unsplash.com/photo-1542296332-2e4473faf563?auto=format&fit=crop&w=2000&q=80" alt="Aircraft on the tarmac at sunrise" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-sky-50/95 via-white/75 to-white/30" />
        <div className="relative max-w-5xl mx-auto px-5 py-12">
          <div className="flex items-center gap-3">
            <span className="inline-flex p-2.5 rounded-2xl bg-gradient-to-br from-sky-700 via-blue-700 to-indigo-600 text-white shadow-xl shrink-0"><Plane className="w-6 h-6 sm:w-8 sm:h-8" /></span>
            <h1 className="font-heading font-900 text-3xl sm:text-4xl lg:text-5xl leading-none tracking-tight">
              <span className="text-blue-800">Indian</span><span className="text-orange-500">Airports</span><span className="text-zinc-500 text-xl sm:text-2xl">.com</span><span className="text-zinc-400 text-xs sm:text-sm align-super">™</span>
            </h1>
          </div>
          <p className="mt-3 text-sm sm:text-base font-700 text-zinc-800">Your Gateway to Airlines & Airports across India</p>
          <p className="mt-1 text-xs sm:text-sm text-zinc-600 max-w-xl">{data.total} airlines serve the airports in our directory. Every airline gets a profile showing the Indian airports it connects.</p>
          <div className="mt-6 flex flex-wrap gap-x-8 gap-y-4" data-testid="india-airlines-quicklinks">
            <HeroQuickLink icon={Plane} l1="Airlines" l2="Information" href="#india-airlines-grid" testid="ql-airlines" />
            <HeroQuickLink icon={MapPin} l1="Airports" l2="Guide" to="/india/airports/directory" testid="ql-airports" />
            <HeroQuickLink icon={Route} l1="Routes &" l2="Destinations" to="/destinations" testid="ql-routes" />
            <HeroQuickLink icon={Star} l1="Mileage Programs" l2="& Loyalty" to="/loyalty" testid="ql-mileage" />
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-5 py-8">
      <div id="india-airlines-grid" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 scroll-mt-24" data-testid="india-airlines-grid">
        {data.items.map((al) => (
          <Link key={al.slug} to={`/india/airlines/${al.slug}`} data-testid={`india-airline-${al.slug}`}
            className="bg-white border border-zinc-200 rounded-2xl px-4 py-4 hover:border-blue-400 hover:-translate-y-0.5 transition-[transform,border-color]">
            <div className="flex items-center gap-2">
              <span className="inline-flex p-2 rounded-xl bg-blue-50 text-blue-700"><Plane className="w-4 h-4" /></span>
              <span className="font-heading font-800">{al.name}</span>
            </div>
            <div className="mt-2 text-xs text-zinc-500 flex items-center gap-3">
              <span>{al.airports_served} Indian airport{al.airports_served > 1 ? "s" : ""}</span>
              {al.hub_count > 0 && <span className="inline-flex items-center gap-1 text-emerald-700 font-700"><TowerControl className="w-3 h-3" /> {al.hub_count} hub{al.hub_count > 1 ? "s" : ""}</span>}
            </div>
          </Link>
        ))}
      </div>
      </div>
    </div>
  );
}

function HeroQuickLink({ icon: Icon, l1, l2, to, href, testid }) {
  const inner = (
    <>
      <span className="inline-flex p-2.5 rounded-xl bg-blue-900 text-white shadow-md group-hover:bg-blue-700 transition-colors"><Icon className="w-5 h-5" /></span>
      <span className="mt-2 block text-xs font-800 text-zinc-800 leading-snug">{l1}<br />{l2}</span>
    </>
  );
  const cls = "group text-center w-[118px]";
  return to
    ? <Link to={to} className={cls} data-testid={`india-airlines-${testid}`}>{inner}</Link>
    : <a href={href} className={cls} data-testid={`india-airlines-${testid}`}>{inner}</a>;
}
